<template>
  <v-row class="ma-2 mpc-map">
    <ModalClusterConfigWizard
      v-model:visible="wizardVisible"
      :criticality="criticality"
      :requireGPU="requireGPU"
      @update:criticality="$emit('update:criticality', $event)"
      @update:requireGPU="$emit('update:requireGPU', $event)"
      @update:selectedCountry="
        $emit('update:selectedCountry', refMap.onSelectCountry($event))
      "
    />
    <v-col class="pa-0 mr-2 col-left">
      <v-row class="ma-0 ml-2 top-row">
        <v-col class="pa-0 pl-2">
          <v-row class="ma-0 token-text-h5"> Wizard </v-row>
          <v-row class="ma-0">
            <CustomButton
              class="my-auto full-width"
              :level="$enums.ButtonLevels.Primary"
              @click="wizardVisible = true"
            >
              Open wizard
            </CustomButton>
          </v-row>
        </v-col>
        <v-col class="pa-0 ml-4 mr-6">
          <v-row class="ma-0 token-text-h5"> Criticality </v-row>
          <v-row class="ma-0">
            <CustomSelect
              :items="criticalityListItems"
              v-model="criticalityListItem"
              @update:modelValue="$emit('update:criticality', $event.value)"
            />
          </v-row>
        </v-col>
      </v-row>
      <v-row class="ma-0">
        <!-- Col locations -->
        <v-col class="pa-0 px-3 col-locations">
          <template v-if="filteredLocations?.length">
            <v-row
              class="ma-0 token-text-h5 pl-1"
              v-if="filteredLocationsInView.length"
            >
              <Icon icon="eye" class="ma-0 mr-2" />
              <span> In view </span>
              <CustomButton
                class="ml-auto my-auto"
                :level="$enums.ButtonLevels.SmallIcon"
                @click="inViewCardsDeployed = !inViewCardsDeployed"
              >
                <Icon
                  icon="caret-down"
                  class="caret"
                  :class="{ invert: inViewCardsDeployed }"
                />
              </CustomButton>
            </v-row>
            <HeightDeployer v-model="inViewCardsDeployed" class="px-1">
              <v-row
                class="ma-0"
                v-for="(location, index) of filteredLocationsInView"
                :key="'location-card-' + index"
              >
                <Tooltip
                  :hoverOnly="true"
                  forcedPosition="right"
                  :disabled="
                    location.attributes.available_server_typologies.length !== 0
                  "
                  class="full-width"
                >
                  <template #target>
                    <CardLocation
                      class="pointer my-1"
                      :location="location"
                      :disabled="
                        location.attributes.available_server_typologies
                          .length === 0
                      "
                      :minPrice="
                        minPricesByLocationId[location.id]
                          ? $helpers.prices.format(
                              minPricesByLocationId[location.id],
                              currency,
                              0,
                            )
                          : ''
                      "
                      :selected="currentLocation?.id === location.id"
                      @click="
                        if (
                          location.attributes.available_server_typologies
                            .length !== 0
                        )
                          selectLocation(location);
                      "
                    />
                  </template>
                  <template #content>
                    This location is temporarily unavailable.<br />
                    This can be due to a lack of server availability, or you
                    might need to connect your own account for that cloud
                    provider.
                  </template>
                </Tooltip>
              </v-row>
            </HeightDeployer>
            <v-row
              class="ma-0 token-text-h5 pl-1"
              :class="{ 'mt-4': !!filteredLocationsInView?.length }"
              v-if="filteredLocationsNotInView.length"
            >
              <Icon icon="eye-off" class="ma-0 mr-2" />
              <span> Out of view </span>
              <CustomButton
                class="ml-auto my-auto"
                :level="$enums.ButtonLevels.SmallIcon"
                @click="outOfViewCardsDeployed = !outOfViewCardsDeployed"
              >
                <Icon
                  icon="caret-down"
                  class="caret"
                  :class="{ invert: outOfViewCardsDeployed }"
                />
              </CustomButton>
            </v-row>
            <HeightDeployer v-model="outOfViewCardsDeployed" class="px-1">
              <v-row
                class="ma-0"
                v-for="(location, index) of filteredLocationsNotInView"
                :key="'location-card-' + index"
              >
                <Tooltip
                  :hoverOnly="true"
                  forcedPosition="right"
                  :disabled="
                    location.attributes.available_server_typologies.length !== 0
                  "
                  class="full-width"
                >
                  <template #target>
                    <CardLocation
                      class="pointer my-1"
                      :dashed="true"
                      :disabled="
                        location.attributes.available_server_typologies
                          .length === 0
                      "
                      :location="location"
                      :minPrice="
                        minPricesByLocationId[location.id]
                          ? $helpers.prices.format(
                              minPricesByLocationId[location.id],
                              currency,
                              0,
                            )
                          : ''
                      "
                      :selected="currentLocation?.id === location.id"
                      @click="
                        if (
                          location.attributes.available_server_typologies
                            .length !== 0
                        )
                          selectLocation(location);
                      "
                    />
                  </template>
                  <template #content>
                    This location is temporarily unavailable.<br />
                    This can be due to a lack of server availability, or you
                    might need to connect your own account for that cloud
                    provider.
                  </template>
                </Tooltip>
              </v-row>
            </HeightDeployer>
          </template>
          <template v-else>
            <v-row
              v-if="filteredLocations.length === 0"
              class="ma-0 d-flex full-height align-center justify-center"
            >
              <v-col class="pa-0">
                <v-row class="ma-0 justify-center mb-4">
                  <Logo logo="fransys-sad" class="pl-4" />
                </v-row>
                <v-row class="ma-0 token-text-mediumbold justify-center">
                  No result found
                </v-row>
                <v-row class="ma-0 token-text-regular justify-center">
                  Please try widening your search criteria.
                </v-row>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
    </v-col>
    <v-col class="pa-0">
      <v-row class="ma-0 top-row">
        <!-- Price slider -->
        <v-col class="pa-0">
          <v-row class="ma-0 token-text-h5">
            From ({{ $enums.CurrencyTrigramToSymbol[currency] }}) - To ({{
              $enums.CurrencyTrigramToSymbol[currency]
            }})
          </v-row>
          <v-row class="ma-0 mt-n2">
            <Slider
              :values="[0, maxOfMinsPrice]"
              :step="1"
              :min="0"
              :max="maxOfMinsPrice"
              :hideThumbLabels="true"
              :hideTicks="true"
              :displayValuesInline="true"
              :valuesInlineAppend="$enums.CurrencyTrigramToSymbol[currency]"
              :range="true"
              @updateValue:values="onSelectPriceFilter"
            />
          </v-row>
        </v-col>
        <!-- / Price slider -->

        <!-- Provider -->
        <v-col class="pa-0 ml-4">
          <v-row class="ma-0 token-text-h5"> Provider </v-row>
          <v-row class="ma-0 mb-2">
            <CustomSelect
              :items="providerListItems"
              v-model="providerFilterListItem"
              @update:modelValue="onSelectProviderFilter"
            />
          </v-row>
        </v-col>
        <!-- / Provider -->

        <!-- GPU -->
        <v-col class="pa-0 ml-4 fit-content">
          <v-row class="ma-0 token-text-h5"> Require GPU </v-row>
          <v-row class="ma-0 mt-2">
            <Switch
              :modelValue="requireGPU"
              @update:modelValue="$emit('update:requireGPU', $event)"
            />
          </v-row>
        </v-col>
        <!-- / GPU -->

        <!-- Renewable energy -->
        <v-col class="pa-0 ml-4 fit-content">
          <v-row class="ma-0 token-text-h5 token-text-color-secondary">
            Renewable energy
            <BadgeSoon class="ml-1 mt-1" />
          </v-row>
          <v-row class="ma-0">
            <CustomNumberInput
              class="full-width"
              placeholder="No filter"
              :disabled="true"
              :disabledVisible="true"
              :selectedUnit="{ label: '%', ratio: 1 }"
              :nullable="true"
              :min="0"
              :max="100"
              :delay="delayOnInput"
              v-model="minRenewableEnergyFilter"
            />
          </v-row>
        </v-col>
        <!-- / Renewable energy -->

        <!-- Architecture -->
        <!-- <v-col class="pa-0 ml-4">
          <v-row class="ma-0 token-text-h5 token-text-color-secondary">
            Architecture
            <BadgeSoon class="ml-1 mt-1"/>
          </v-row>
          <v-row class="ma-0">
            <CustomSelect :disabled="true" :items="architectureFilterListItems" v-model="architectureFilterListItem"/>
          </v-row>
        </v-col> -->
        <!-- / Architecture -->

        <!-- Certifications -->
        <v-col class="pa-0 ml-4 fit-content">
          <v-row class="ma-0 token-text-h5 token-text-color-secondary">
            Certifications
            <BadgeSoon class="ml-1 mt-1" />
          </v-row>
          <v-row class="ma-0">
            <CustomSelect
              :disabled="true"
              :items="certicifationsFilterListItems"
              v-model="certicifationsFilterListItem"
            />
          </v-row>
        </v-col>
        <!-- / Certifications -->
      </v-row>
      <v-row class="ma-0">
        <Map
          v-if="showMap"
          height="calc(100vh - 196px);"
          width="100%"
          :markers="markers"
          v-model:markersInView="markersInView"
          ref="refMap"
          @clickMapMarker="onClickMapMarker"
        />
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Vue, Options, prop } from "vue-class-component";
import { MapMarker } from "@/types";
import {
  APIClusterLocation,
  APIClusterNodeTypologie,
  APIClusterRequirement,
  APIInfrastructureProvider,
} from "@/typesAPI";
import { ListItem } from "@/components/UIElements/List.vue";
import { Watch } from "vue-property-decorator";
import { ref } from "vue";

import Map from "@/components/UIElements/Map.vue";
import CustomButton from "@/components/UIElements/CustomButton.vue";
import CustomSelect from "@/components/UIElements/CustomSelect.vue";
import CustomNumberInput from "@/components/UIElements/CustomNumberInput.vue";
import BadgeSoon from "@/components/UIElements/Preinstanced/BadgeSoon.vue";
import CardLocation from "@/components/UIElements/CardLocation.vue";
import HeightDeployer from "@/components/UIElements/HeightDeployer.vue";
import Logo from "@/components/UIElements/Logo.vue";
import Icon from "@/components/UIElements/Icon.vue";
import Switch from "@/components/UIElements/Switch.vue";
import Slider from "@/components/UIElements/Slider.vue";
import Tooltip from "@/components/UIElements/Tooltip.vue";

import ModalClusterConfigWizard from "@/components/Modals/ModalClusterConfigWizard.vue";

class Props {
  criticality: string = prop({
    required: true,
  });
  location: APIClusterLocation | null = prop({
    required: true,
  });
  provider: string = prop({
    required: true,
  });
  requireGPU: boolean = prop({
    required: true,
  });
  createMode?: boolean = prop({
    required: false,
  });
  showMap?: boolean = prop({
    required: false,
  });
}

@Options({
  components: {
    Map,
    CustomButton,
    CustomSelect,
    CustomNumberInput,
    BadgeSoon,
    CardLocation,
    HeightDeployer,
    Logo,
    Icon,
    Switch,
    Slider,
    Tooltip,
    ModalClusterConfigWizard,
  },
})
export default class MPCMap extends Vue.with(Props) {
  refMap: any = ref(null);
  inViewCardsDeployed = true;
  outOfViewCardsDeployed = true;
  wizardVisible = false;
  delayOnInput = 500;
  markersInView: MapMarker[] = [];
  architectureFilterListItem: ListItem = {};
  architectureFilterListItems: ListItem[] = [];
  certicifationsFilterListItem: ListItem = {};
  certicifationsFilterListItems: ListItem[] = [];
  criticalityListItem: ListItem = {};
  providerFilterListItem: ListItem = {};
  providerFilter: APIInfrastructureProvider | null = null;
  currentLocation: APIClusterLocation | null = null;
  priceFilterActif: { min: number | null; max: number | null } = {
    min: 0,
    max: null,
  };
  minRenewableEnergyFilter: number | null = null;

  get locations(): APIClusterLocation[] {
    if (!this.providers.length) {
      return [];
    }
    let ret: APIClusterLocation[] = [];
    this.providers.forEach((prov) => {
      ret.push(
        ...this.$store.getters["clusters/getLocationsByProvider"](prov?.id),
      );
    });

    return ret;
  }

  get filteredLocations(): APIClusterLocation[] {
    if (!this.locations.length) {
      return [];
    }
    return this.locations.filter((location) => {
      if (
        this.providerFilter &&
        this.providerFilter.id !== "none" &&
        this.providerFilter.id !== location.attributes.provider
      ) {
        return false;
      }

      if (this.priceFilterActif.min !== null) {
        const minPrice = this.minPricesByLocationId[location.id] ?? 0;
        if (Math.round(minPrice / 100) < this.priceFilterActif.min) {
          return false;
        }
      }

      if (this.priceFilterActif.max !== null) {
        const minPrice = this.minPricesByLocationId[location.id] ?? 0;
        if (Math.round(minPrice / 100) > this.priceFilterActif.max) {
          return false;
        }
      }
      // if(this.minRenewableEnergyFilter !== null && this.minRenewableEnergyFilter > 0) {
      //   if(location.attributes.renewable_energy_percentage < this.minRenewableEnergyFilter) {
      //     return false
      //   }
      // }

      const locationNodeTypologies = this.nodeTypologies.filter((typo) =>
        location.attributes.available_server_typologies.includes(typo.id),
      );
      if (
        this.requireGPU &&
        !locationNodeTypologies.find((typo) => typo.attributes.gpus.length > 0)
      ) {
        return false;
      }
      return true;
    });
  }

  get filteredLocationsInView(): APIClusterLocation[] {
    if (!this.markersInView.length) {
      return [];
    }
    return this.filteredLocations.filter((location) => {
      return this.markersInView.find((marker) => marker.id === location.id);
    });
  }

  get filteredLocationsNotInView(): APIClusterLocation[] {
    return this.filteredLocations.filter((location) => {
      return !this.filteredLocationsInView.find(
        (loc) => loc.id === location.id,
      );
    });
  }

  get providers(): APIInfrastructureProvider[] {
    return this.$store.getters["clusters/getProviders"];
  }

  get criticalityListItems(): ListItem[] {
    return Object.keys(this.$enums.ClusterCriticalityLabels).map((key) => ({
      text: this.$enums.ClusterCriticalityLabels[key],
      value: key,
    }));
  }

  get providerListItems(): ListItem[] {
    if (!this.providers.length) {
      return [];
    }
    const ret = [
      {
        text: "No filter",
        value: "none",
      },
    ];
    ret.push(
      ...this.providers.map((infrastructureProvider) => ({
        text: infrastructureProvider.attributes.name,
        value: infrastructureProvider.id,
        logo: infrastructureProvider.id,
      })),
    );

    return ret;
  }

  get clusterRequirements(): APIClusterRequirement[] {
    const ret: APIClusterRequirement[] = [];
    this.locations.forEach((location) => {
      if (location.attributes.available_server_typologies.length) {
        const filteredReqs = this.$store.getters[
          "clusters/getClusterRequirementsByProviderAndLocation"
        ](location.attributes.provider, location.id).filter(
          (req: APIClusterRequirement) => !ret.find((r) => r.id === req.id),
        );
        ret.push(...filteredReqs);
      }
    });
    return ret;
  }

  get minWorkerNodeRequirements(): number {
    return this.$helpers.clusters.getMinAmountFromRequirements(
      this.clusterRequirements,
      0,
      this.$enums.RequirementTypes.WORKER,
      this.criticality,
    );
  }

  get nodeTypologies(): APIClusterNodeTypologie[] {
    if (!this.locations.length) {
      return [];
    }
    const ret: APIClusterNodeTypologie[] = [];
    this.locations.forEach((location) => {
      if (location.attributes.available_server_typologies.length) {
        const filteredTypo = this.$store.getters[
          "clusters/getClusterNodeTypologiesByProviderAndLocation"
        ](location.attributes.provider, location.id).filter(
          (typo: APIClusterNodeTypologie) => !ret.find((r) => r.id === typo.id),
        );
        ret.push(...filteredTypo);
      }
    });
    return ret;
  }

  get currency(): string {
    return this.$store.getters["user/getCurrency"];
  }

  get markers(): MapMarker[] {
    if (!this.filteredLocations.length) {
      return [];
    }
    return this.filteredLocations.map((location) => ({
      id: location.id,
      lat: Number(location.attributes.latitude),
      lng: Number(location.attributes.longitude),
      city: location.attributes.city,
      selected: this.currentLocation?.id === location.id,
      price: this.minPricesByLocationId[location.id] ?? 0,
      currency: this.currency,
      provider: location.attributes.provider || "",
      countryCode: location.attributes.country || "",
      disabled: location.attributes.available_server_typologies.length === 0,
    }));
  }

  get minPricesByLocationId(): { [key: string]: number } {
    const ret: { [key: string]: number } = {};
    this.locations.forEach((location) => {
      let min = Infinity;
      this.nodeTypologies.forEach((nodeTypo) => {
        if (
          location.attributes.available_server_typologies.includes(
            nodeTypo.id,
          ) &&
          Number(nodeTypo.attributes.price.amount) < min
        ) {
          min = Number(nodeTypo.attributes.price.amount);
        }
      });
      ret[location.id] = min * this.minWorkerNodeRequirements;
    });
    return ret;
  }

  get maxOfMinsPrice(): number {
    let max = 0;
    Object.values(this.minPricesByLocationId).forEach((price) => {
      if (price > max) {
        max = price;
      }
    });
    return Math.round(max / 100);
  }

  onSelectPriceFilter(newVals: number[]) {
    this.priceFilterActif.min = newVals[0] ?? 0;
    this.priceFilterActif.max = newVals[1] ?? null;
    this.removeCurrentLocationIfFiltered();
  }

  onSelectProviderFilter(newProviderListItem: ListItem) {
    this.providerFilter =
      this.providers.find((prov) => prov.id === newProviderListItem.value) ??
      null;
    this.removeCurrentLocationIfFiltered();
  }

  onClickMapMarker(marker: MapMarker) {
    marker.selected = true;
    this.updateLocation(
      this.locations.find((location) => location.id === marker.id) ?? null,
    );
  }

  removeCurrentLocationIfFiltered() {
    this.$nextTick(() => {
      if (
        !this.filteredLocations.find(
          (location) => location.id === this.currentLocation?.id,
        )
      ) {
        this.updateLocation(null);
      }
    });
  }

  updateLocation(location: APIClusterLocation | null) {
    this.currentLocation = location;
    this.$emit("update:location", this.currentLocation);
    this.$emit(
      "update:provider",
      this.currentLocation?.attributes?.provider ?? "",
    );
  }

  selectLocation(location: APIClusterLocation) {
    this.updateLocation(location);
  }

  @Watch("providers", { immediate: true })
  onProvidersChange() {
    if (this.providers.length) {
      this.currentLocation = this.location;
      this.providerFilterListItem =
        this.providerListItems.find((p) => p.value === "none") ?? {};
    }
  }

  @Watch("criticality", { immediate: true })
  onCriticalityChange() {
    if (this.criticality) {
      this.criticalityListItem =
        this.criticalityListItems.find(
          (item) => item.value === this.criticality,
        ) ?? {};
    }
  }

  @Watch("createMode", { immediate: true })
  onCreateModeChange() {
    if (this.createMode && !this.location) {
      this.wizardVisible = true;
    }
  }

  @Watch("filteredLocations")
  onFilteredLocationsChange() {
    // If the selected location is no longer in the filtered list, remove it
    if (
      this.currentLocation &&
      !this.filteredLocations.find((loc) => loc.id === this.currentLocation?.id)
    ) {
      this.updateLocation(null);
    }
  }
}
</script>
<style lang="scss" scoped>
@import "@/css/variables";
.caret {
  transition: transform 0.2s ease-in-out;
  &.invert {
    transform: rotate(-180deg);
  }
}
.top-row {
  height: 84px;
  &::v-deep(.custom-select-input) {
    height: 38px;
  }
}
.col-locations {
  max-height: calc(100vh - 182px);
  min-height: calc(100vh - 182px);
  overflow-y: scroll;
}
.col-left {
  max-width: 400px;
  min-width: 400px;
}
</style>
